import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Pagination } from '@core/models/pagination.model';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { UserService } from '@core/services/user.service';
import {
  DropDownModel,
  DropdownComponent,
} from '@shared/ui/dropdown/dropdown.component';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subscription,
  take,
} from 'rxjs';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { dropdownMenuAnimation } from '@shared/animations/animations';
import { FiltersComponent } from '../shared/ui/filters/filters.component';
import { IFILTER } from 'app/app.constants';
import { ViewProfileListDatum } from '@core/models/admin/users';
import { TableSkeletonLoaderComponent } from '../shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { LoadingService } from '@core/services/loading.service';
import { Store } from '@ngxs/store';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { TableActions } from 'app/auth/table-store/table.actions';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { NotificationFacade } from '@core/facades/notification.facade';
@Component({
  selector: 'app-user-customers',
  standalone: true,
  // changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './customers.component.html',
  styleUrl: './customers.component.scss',
  animations: [dropdownMenuAnimation],
  imports: [
    RouterOutlet,
    CommonModule,
    DropdownComponent,
    ReactiveFormsModule,
    FormsModule,
    TableNavigatorComponent,
    FiltersComponent,
    TableSkeletonLoaderComponent,
    PermissionDirective,
    TableRefreshComponent,
  ],
})
export class CustomersComponent implements OnInit, OnDestroy {
  filterMenuState: 'visible' | 'hidden' = 'hidden';
  DropdownVisible = false;
  subs: Subscription[] = [];
  actionList: DropDownModel[] = [];
  usersList: ViewProfileListDatum[] = [];
  private totalCountSubject = new BehaviorSubject<number>(0);
  totalCount = 0;
  totalCustomers = 0;
  totalCount$: Observable<number> = this.totalCountSubject.asObservable();
  isFetchingCustomerList$!: Observable<boolean>;
  adminService = inject(UserService);
  change = inject(ChangeDetectorRef);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  breadCrumbService = inject(BreadCrumbService);
  store = inject(Store);
  confirmDialogService = inject(ConfirmDialogService);
  notify = inject(NotificationFacade);
  private loadingService = inject(LoadingService);
  private permissionService = inject(RolesAndPermissionsService);

  currentPage: number = 1;
  isFilterActive: boolean = false;
  isFetchingListSubject$ = new BehaviorSubject<boolean>(false);
  isFetchingList$ = this.isFetchingListSubject$.asObservable();
  userListSubject = new BehaviorSubject<any>(null);
  userList$ = this.userListSubject.asObservable();
  private hadFilterBefore = false;

  filterStructure: IFILTER[] = [
    {
      label: 'Status',
      filterKey: 'status',
      type: 'dropdown',
      dropdown: [
        { key: 'activated', value: 'Activated' },
        { key: 'pending', value: 'Pending' },
      ],
    },
    {
      label: 'Profile Status',
      filterKey: 'restricted',
      type: 'dropdown',
      dropdown: [
        { key: 'true', value: 'Restricted' },
        { key: 'false', value: 'Unrestricted' },
      ],
    },
  ];
  constructor() {
    this.userList$ = this.store.select(
      TableSelectors.getTableData('mobileUsers')
    );
    this.totalCount$ = this.store.select(
      TableSelectors.getTableTotalCount('mobileUsers')
    );
    this.isFetchingCustomerList$ = this.store.select(
      TableSelectors.getTableLoading('mobileUsers')
    );
  }

  ngOnInit() {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);
    this.loadUserList();
    this.customerFilterEventListener();

  }

  loadUserList(page = 1) {
    const sub = this.userList$.pipe(take(1)).subscribe((user) => {
      const hasData = Array.isArray(user) && user.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'mobileUsers',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  customerFilterEventListener() {
    this.loadingService.setLoading('view-profile-list', true);
    this.activatedRoute.queryParams
      .pipe(
        debounceTime(300), // Debounce to limit API calls
        distinctUntilChanged() // Only trigger when query params change
      )
      .subscribe((params: any) => {
        let query = '';

        this.filterStructure.forEach((filter) => {
          if (params[filter.filterKey]) {
            query += `${filter.filterKey}=${params[filter.filterKey]}&`;
          }
        });

        if (params['search']) {
          query += `search_text=${params['search']}&`;
        }

        const rawPage = params['page'] ? parseInt(params['page']) : 1;
        const page = rawPage > 1 ? rawPage - 1 : 0;
        const take = 10;
        const skip = page * take;

        if (query) {
          query += `skip=${skip}&take=${take}`;
          this.isFilterActive = true;
          this.hadFilterBefore = true;
          this.store.dispatch(
            new TableActions.FilterMobileUsers({ filter: query })
          );
        } else {
          // only re-fetch if there was a previous filter
          if (this.hadFilterBefore || params['page']) {
            this.hadFilterBefore = false;
            this.store.dispatch(
              new TableActions.LoadTableData({
                tableKey: 'mobileUsers',
                params: new Pagination({ skip: skip, size: take }),
              })
            );
          }
        }
      });
  }

  registerationDropdownActions(): DropDownModel[] {
    return [
      {
        title: 'Individual',
        action: () => this.goToIndividualReg(),
        show: true && this.permissionService.hasPermission(['create_user']),
      },
      {
        title: 'Corporate',
        action: () => this.goToCorporateReg(),
        show:
          true && this.permissionService.hasPermission(['create_coroporate']),
      },
    ];
  }
  goToIndividualReg() {
    this.router.navigateByUrl('/admin/accounts/ind-personal-details');
  }
  goToCorporateReg() {
    this.router.navigateByUrl('/admin/accounts/corp-company-details');
  }

  navigate(currentPage: number) {
    if (this.isFilterActive) {
      this.router.navigate([], {
        queryParams: { page: currentPage },
        queryParamsHandling: 'merge',
      });
    }
  }

  getAdminList(page: Pagination = new Pagination()) {
    this.isFetchingListSubject$.next(true);
    const sub = this.adminService.viewProfileList(page).subscribe({
      next: (res) => {
        this.isFetchingListSubject$.next(false);
        this.usersList = res.data;
        this.totalCountSubject.next(res.total_count);
        this.totalCustomers = res.total_count;
        this.change.detectChanges();
      },
      error: () => {
        this.isFetchingListSubject$.next(false);
      },
    });
    this.subs.push(sub);
  }

  initDropdownList(user: ViewProfileListDatum) {
    return (this.actionList = [
      {
        action: () => {
          this.updateUserProfileAccess(user, 'restrict');
        },
        title: 'Restrict',
        show:
          (
            !user.user_restriction?.length ||
            !user.user_restriction.some(r => r.is_restricted)
          ) &&
          this.permissionService.hasPermission(['can_restrict_account']),
      },
      {
        action: () => {
          this.updateUserProfileAccess(user, 'unrestrict');
        },
        title: 'Unrestrict',
        show:
          user.user_restriction.some(r => r.is_restricted) &&
          this.permissionService.hasPermission(['can_unrestrict_account']),
      },
    ]);
  }

  updateUserProfileAccess(
    user: ViewProfileListDatum,
    status: 'restrict' | 'unrestrict'
  ) {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    //Get id for restrictions that are true
    const id = status === 'restrict' ? user.id : (user.user_restriction.find(r => r.is_restricted)?.id || '');
    const sub = this.adminService
      .updateUserProfileAccess(id, status)
      .subscribe({
        next: () => {
          this.confirmDialogService.updateDialog({
            isDialogLoading: false,
            isOpen: false,
          });
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'mobileUsers',
              params: new Pagination({ skip: this.currentPage * 10, size: 10 }),
            })
          );

          this.notify.success(`Successfully ${status}ed user`);
          this.notify.success(`Successfully ${status}ed user`);
        },
        error: (err) => {
          this.confirmDialogService.updateDialog({
            isDialogLoading: false,
            isOpen: false,
          });
          this.notify.error('Failed to update user, try again');
        },
      });
    this.subs.push(sub);
  }

  getProfileAccessStatus(user: ViewProfileListDatum) {
    if (user.user_restriction.length == 0 || !user.user_restriction.some(r => r.is_restricted)) {
      return 'Unrestricted';
    } else {
      return 'Restricted';
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
